import React, { useState, useRef, useEffect } from 'react';
import { marked } from 'marked';
import { safeImport } from '../utils/safeImport.js';

// ─── Small helpers ────────────────────────────────────────────────────────────
const TBtn = ({ onClick, title, children }) => (
    <button
        type="button"
        title={title}
        onMouseDown={e => { e.preventDefault(); onClick(); }}
        className="rounded-md px-2 py-1 text-slate-400 hover:text-white hover:bg-white/10 transition text-xs font-medium select-none"
    >
        {children}
    </button>
);

const TDivider = () => <div className="h-4 w-px bg-white/10 mx-0.5 shrink-0" />;

// ─── Component ────────────────────────────────────────────────────────────────
const FlashNotesViewer = ({ folderName, notes, onClose, onSave }) => {
    const [mode, setMode] = useState('preview');
    const [draft, setDraft] = useState(notes || '');
    const [savedSnapshot, setSavedSnapshot] = useState(notes || '');
    const [copyDone, setCopyDone] = useState(false);
    const [pdfError, setPdfError] = useState(null);
    const textareaRef = useRef(null);
    const isDirty = draft !== savedSnapshot;

    useEffect(() => {
        if (mode === 'edit') textareaRef.current?.focus();
    }, [mode]);

    // ─── Save / discard ───────────────────────────────────────────────────
    const doSave = () => {
        onSave?.(draft);
        setSavedSnapshot(draft);
    };

    const handleSwitchToPreview = () => {
        if (isDirty) doSave();
        setMode('preview');
    };

    const handleDiscard = () => {
        setDraft(savedSnapshot);
        setMode('preview');
    };

    // ─── Formatting helpers (operate on textarea) ─────────────────────────
    const wrapSel = (prefix, suffix = prefix) => {
        const el = textareaRef.current;
        if (!el) return;
        const s = el.selectionStart, e = el.selectionEnd;
        const selected = draft.slice(s, e);
        const next = draft.slice(0, s) + prefix + selected + suffix + draft.slice(e);
        setDraft(next);
        setTimeout(() => {
            el.focus();
            el.setSelectionRange(s + prefix.length, e + prefix.length);
        }, 0);
    };

    const prefixLine = (prefix) => {
        const el = textareaRef.current;
        if (!el) return;
        const s = el.selectionStart;
        const lineStart = draft.lastIndexOf('\n', s - 1) + 1;
        // Toggle: remove prefix if already present
        if (draft.slice(lineStart, lineStart + prefix.length) === prefix) {
            const next = draft.slice(0, lineStart) + draft.slice(lineStart + prefix.length);
            setDraft(next);
            setTimeout(() => { el.focus(); el.setSelectionRange(s - prefix.length, s - prefix.length); }, 0);
        } else {
            const next = draft.slice(0, lineStart) + prefix + draft.slice(lineStart);
            setDraft(next);
            setTimeout(() => { el.focus(); el.setSelectionRange(s + prefix.length, s + prefix.length); }, 0);
        }
    };

    const insertBlock = (text) => {
        const el = textareaRef.current;
        if (!el) return;
        const s = el.selectionStart;
        const needNL = s > 0 && draft[s - 1] !== '\n';
        const insert = (needNL ? '\n' : '') + text + '\n';
        const next = draft.slice(0, s) + insert + draft.slice(s);
        setDraft(next);
        setTimeout(() => { el.focus(); el.setSelectionRange(s + insert.length, s + insert.length); }, 0);
    };

    // Keyboard shortcuts inside textarea
    const handleKeyDown = (e) => {
        if ((e.ctrlKey || e.metaKey) && e.key === 'b') { e.preventDefault(); wrapSel('**'); }
        if ((e.ctrlKey || e.metaKey) && e.key === 'i') { e.preventDefault(); wrapSel('*'); }
        if ((e.ctrlKey || e.metaKey) && e.key === 's') { e.preventDefault(); doSave(); }
    };

    // ─── Export handlers ──────────────────────────────────────────────────
    const handleCopy = () => {
        navigator.clipboard?.writeText(draft).catch(() => {});
        setCopyDone(true);
        setTimeout(() => setCopyDone(false), 2000);
    };

    const handlePrint = () => {
        const html = marked(draft);
        const win = window.open('', '_blank');
        if (!win) return;
        win.document.write(`<!DOCTYPE html><html><head>
            <meta charset="utf-8">
            <title>Flash Notes — ${folderName}</title>
            <style>
                * { box-sizing: border-box; margin: 0; padding: 0; }
                body { font-family: Georgia, 'Times New Roman', serif; max-width: 700px; margin: 48px auto; color: #1e1b4b; line-height: 1.75; padding: 0 24px; }
                .doc-header { text-align: center; margin-bottom: 2.5rem; padding-bottom: 1.5rem; border-bottom: 2px solid #0d9488; }
                .doc-header .brand { font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.15em; color: #0f766e; font-family: system-ui, sans-serif; }
                .doc-header h1 { font-size: 1.75rem; color: #115e59; margin-top: 0.5rem; }
                h2 { font-size: 1.3rem; color: #047857; margin: 2rem 0 0.6rem; }
                h3 { font-size: 1.05rem; color: #0f766e; margin: 1.5rem 0 0.4rem; }
                p { margin-bottom: 0.9rem; }
                ul, ol { margin: 0 0 0.9rem 1.5rem; }
                li { margin-bottom: 0.35rem; }
                strong { color: #065f46; }
                blockquote { border-left: 3px solid #0d9488; margin: 0.5rem 0 1rem; padding: 0.5rem 1rem; background: #f0fdfa; color: #047857; font-style: italic; }
                code { background: #f3f4f6; padding: 0.15em 0.4em; border-radius: 3px; font-size: 0.87em; font-family: monospace; }
                pre { background: #f3f4f6; padding: 1rem; border-radius: 6px; overflow: auto; margin-bottom: 1rem; }
                pre code { background: none; padding: 0; }
                table { width: 100%; border-collapse: collapse; margin-bottom: 1rem; font-size: 0.9em; }
                th { background: #ccfbf1; padding: 0.5rem 0.75rem; text-align: left; border-bottom: 2px solid #0d9488; font-family: system-ui, sans-serif; }
                td { padding: 0.4rem 0.75rem; border-bottom: 1px solid #e5e7eb; }
                hr { border: none; border-top: 1px solid #e5e7eb; margin: 1.5rem 0; }
                @media print { body { margin: 0; padding: 24px; } }
            </style>
        </head><body>
            <div class="doc-header">
                <div class="brand">FlashFonic · Flash Notes</div>
                <h1>${folderName}</h1>
            </div>
            ${html}
        </body></html>`);
        win.document.close();
        win.focus();
        setTimeout(() => win.print(), 250);
    };

    const handleEmail = () => {
        const subject = encodeURIComponent(`Flash Notes: ${folderName}`);
        const plain = draft.replace(/[#*`>_~]/g, '').slice(0, 1800);
        window.location.href = `mailto:?subject=${subject}&body=${encodeURIComponent(plain)}`;
    };

    const handlePDF = async () => {
        setPdfError(null);
        try {
            const { jsPDF } = await safeImport(() => import('jspdf'), 'PDF export');
            const { exportNotesToPDF } = await safeImport(() => import('../utils/pdf.js'), 'PDF export');
            exportNotesToPDF(folderName, draft, jsPDF, marked);
        } catch (e) {
            console.error('PDF export failed', e);
            setPdfError(e?.message || 'PDF export failed.');
        }
    };

    const wordCount = draft.trim() ? draft.trim().split(/\s+/).length : 0;
    const renderedHtml = marked(draft);

    return (
        <div
            className="fixed inset-0 z-50 flex items-end sm:items-center justify-center"
            style={{ backdropFilter: 'blur(18px)', backgroundColor: 'rgba(5,8,22,0.88)' }}
            onClick={onClose}
        >
            <div
                className="flex flex-col w-full sm:max-w-4xl rounded-t-3xl sm:rounded-3xl border border-white/10 shadow-2xl shadow-teal-900/25 overflow-hidden"
                style={{ maxHeight: '94dvh', background: 'linear-gradient(155deg, #111828 0%, #0c1020 100%)' }}
                onClick={e => e.stopPropagation()}
            >
                {/* Mobile drag handle */}
                <div className="flex justify-center pt-3 pb-1 sm:hidden shrink-0">
                    <div className="h-1 w-10 rounded-full bg-white/20" />
                </div>

                {/* Top glow line */}
                <div className="h-px w-full bg-gradient-to-r from-transparent via-teal-500/50 to-transparent shrink-0" />

                {/* ── Header ─────────────────────────────────────────────────────── */}
                <div className="flex items-center justify-between px-5 py-3.5 border-b border-white/8 shrink-0">
                    <div className="flex items-center gap-3 min-w-0">
                        <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-teal-500/25 to-emerald-500/20 ring-1 ring-teal-400/20 shrink-0">
                            <svg className="h-4 w-4 text-teal-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                        </div>
                        <div className="min-w-0">
                            <h2 className="text-sm font-semibold text-white leading-tight">Flash Notes</h2>
                            <p className="text-[11px] text-slate-500 truncate">{folderName}</p>
                        </div>
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                        {/* Preview / Edit segmented control */}
                        <div className="flex items-center rounded-lg bg-white/5 ring-1 ring-white/8 p-0.5 gap-0.5">
                            <button
                                type="button"
                                onClick={handleSwitchToPreview}
                                className={`rounded-md px-3 py-1 text-xs font-medium transition ${mode === 'preview' ? 'bg-teal-600 text-white shadow-sm' : 'text-slate-400 hover:text-white'}`}
                            >
                                Preview
                            </button>
                            <button
                                type="button"
                                onClick={() => setMode('edit')}
                                className={`rounded-md px-3 py-1 text-xs font-medium transition ${mode === 'edit' ? 'bg-teal-600 text-white shadow-sm' : 'text-slate-400 hover:text-white'}`}
                            >
                                Edit
                            </button>
                        </div>

                        {isDirty && mode === 'edit' && (
                            <span className="hidden sm:block text-[10px] text-amber-400 font-medium">unsaved</span>
                        )}

                        <button onClick={onClose} className="rounded-lg p-1.5 text-slate-400 hover:text-white hover:bg-white/8 transition" aria-label="Close">
                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                </div>

                {/* ── Formatting toolbar (edit mode) ────────────────────────────── */}
                {mode === 'edit' && (
                    <div className="flex items-center gap-0.5 px-3 py-2 border-b border-white/8 bg-black/20 overflow-x-auto shrink-0 scrollbar-none">
                        {/* Text style */}
                        <TBtn onClick={() => wrapSel('**')} title="Bold (Ctrl+B)">
                            <span className="font-bold text-[13px]">B</span>
                        </TBtn>
                        <TBtn onClick={() => wrapSel('*')} title="Italic (Ctrl+I)">
                            <span className="italic text-[13px]">I</span>
                        </TBtn>
                        <TBtn onClick={() => wrapSel('~~')} title="Strikethrough">
                            <span className="line-through text-[12px]">S</span>
                        </TBtn>
                        <TBtn onClick={() => wrapSel('`', '`')} title="Inline code">
                            <span className="font-mono text-[11px] text-teal-300">{`</>`}</span>
                        </TBtn>

                        <TDivider />

                        {/* Headings */}
                        <TBtn onClick={() => prefixLine('# ')} title="Heading 1">
                            <span className="font-bold text-[11px]">H1</span>
                        </TBtn>
                        <TBtn onClick={() => prefixLine('## ')} title="Heading 2">
                            <span className="font-bold text-[11px]">H2</span>
                        </TBtn>
                        <TBtn onClick={() => prefixLine('### ')} title="Heading 3">
                            <span className="font-bold text-[11px]">H3</span>
                        </TBtn>

                        <TDivider />

                        {/* Lists */}
                        <TBtn onClick={() => prefixLine('- ')} title="Bullet list">
                            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                            </svg>
                        </TBtn>
                        <TBtn onClick={() => prefixLine('1. ')} title="Numbered list">
                            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" />
                            </svg>
                        </TBtn>
                        <TBtn onClick={() => prefixLine('> ')} title="Blockquote">
                            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                            </svg>
                        </TBtn>

                        <TDivider />

                        {/* Blocks */}
                        <TBtn onClick={() => insertBlock('---')} title="Horizontal rule">
                            <span className="text-[12px]">—</span>
                        </TBtn>
                        <TBtn onClick={() => insertBlock('```\n\n```')} title="Code block">
                            <span className="font-mono text-[10px] text-teal-300">{ '{  }' }</span>
                        </TBtn>

                        {/* Save / discard (right-side in toolbar for mobile) */}
                        {isDirty && (
                            <div className="ml-auto flex items-center gap-1.5 pl-2">
                                <button
                                    type="button"
                                    onClick={handleDiscard}
                                    className="rounded-md px-2.5 py-1 text-xs text-slate-400 hover:text-white hover:bg-white/8 transition"
                                >
                                    Discard
                                </button>
                                <button
                                    type="button"
                                    onClick={doSave}
                                    className="rounded-md px-2.5 py-1 text-xs font-semibold text-white bg-teal-600 hover:bg-teal-500 transition"
                                >
                                    Save
                                </button>
                            </div>
                        )}
                    </div>
                )}

                {/* ── Content ───────────────────────────────────────────────────── */}
                <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain" style={{ WebkitOverflowScrolling: 'touch' }}>
                    {mode === 'edit' ? (
                        <textarea
                            ref={textareaRef}
                            value={draft}
                            onChange={e => setDraft(e.target.value)}
                            onKeyDown={handleKeyDown}
                            className="w-full h-full min-h-[380px] px-6 py-5 bg-transparent text-slate-200 text-sm leading-7 resize-none outline-none font-mono placeholder-slate-600"
                            placeholder={'Start writing…\n\n# Heading\n## Section\n- Bullet point\n**Bold**, *italic*, `code`'}
                            spellCheck
                        />
                    ) : (
                        <div className="px-6 py-6 sm:px-8">
                            <style dangerouslySetInnerHTML={{ __html: `
                                .fn-body { max-width: 70ch; }
                                .fn-body h1 {
                                    background: linear-gradient(90deg,#14b8a6,#10b981,#06b6d4);
                                    -webkit-background-clip:text; background-clip:text; color:transparent;
                                    font-size:1.65rem; font-weight:800; letter-spacing:-0.02em; margin:0 0 1.1rem; line-height:1.2;
                                }
                                .fn-body h2 {
                                    background: linear-gradient(90deg,#10b981,#5eead4);
                                    -webkit-background-clip:text; background-clip:text; color:transparent;
                                    font-size:1.2rem; font-weight:700; margin:2.2rem 0 0.65rem; line-height:1.3;
                                }
                                .fn-body h3 { color:#2dd4bf; font-size:1.02rem; font-weight:600; margin:1.75rem 0 0.45rem; }
                                .fn-body p { color:#cbd5e1; margin:0 0 0.95rem; line-height:1.8; font-size:0.9rem; }
                                .fn-body ul { color:#cbd5e1; margin:0 0 0.95rem 1.4rem; }
                                .fn-body ol { color:#cbd5e1; margin:0 0 0.95rem 1.4rem; }
                                .fn-body li { margin-bottom:0.3rem; line-height:1.7; font-size:0.9rem; }
                                .fn-body strong { color:#e2e8f0; font-weight:600; }
                                .fn-body em { color:#5eead4; font-style:italic; }
                                .fn-body del { color:#64748b; }
                                .fn-body blockquote { border-left:3px solid #14b8a6; margin:0.5rem 0 1.1rem; padding:0.6rem 1rem; background:rgba(20,184,166,0.07); border-radius:0 8px 8px 0; }
                                .fn-body blockquote p { color:#2dd4bf; margin:0; font-style:italic; font-size:0.875rem; }
                                .fn-body code { background:rgba(255,255,255,0.08); color:#5eead4; padding:0.12em 0.38em; border-radius:4px; font-size:0.82em; font-family:monospace; }
                                .fn-body pre { background:rgba(255,255,255,0.05); border:1px solid rgba(255,255,255,0.09); border-radius:10px; padding:1rem 1.1rem; margin:0.75rem 0 1.1rem; overflow-x:auto; }
                                .fn-body pre code { background:none; padding:0; color:#6ee7b7; }
                                .fn-body hr { border:none; border-top:1px solid rgba(255,255,255,0.09); margin:1.75rem 0; }
                                .fn-body table { width:100%; border-collapse:collapse; margin-bottom:1.1rem; font-size:0.85em; }
                                .fn-body th { background:rgba(255,255,255,0.05); color:#94a3b8; font-weight:600; padding:0.5rem 0.75rem; text-align:left; border-bottom:1px solid rgba(255,255,255,0.12); }
                                .fn-body td { color:#cbd5e1; padding:0.4rem 0.75rem; border-bottom:1px solid rgba(255,255,255,0.05); }
                                .fn-body a { color:#2dd4bf; text-decoration:underline; }
                            ` }} />
                            {draft ? (
                                <div className="fn-body" dangerouslySetInnerHTML={{ __html: renderedHtml }} />
                            ) : (
                                <div className="flex flex-col items-center justify-center py-16 text-center gap-4">
                                    <div className="h-14 w-14 rounded-2xl bg-teal-500/10 ring-1 ring-teal-400/15 flex items-center justify-center">
                                        <svg className="h-7 w-7 text-teal-400/60" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                        </svg>
                                    </div>
                                    <div>
                                        <p className="text-slate-400 text-sm font-medium">No notes yet</p>
                                        <button onClick={() => setMode('edit')} className="mt-2 text-xs text-teal-400 hover:text-teal-300 transition">
                                            Start writing →
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* ── Footer / export bar ───────────────────────────────────────── */}
                <div className="shrink-0 border-t border-white/8 bg-black/20">
                    {pdfError && (
                        <div className="px-4 pt-2.5 text-xs text-rose-300">{pdfError}</div>
                    )}
                    <div className="flex items-center justify-between px-4 py-3 gap-3">
                        {/* Export buttons */}
                        <div className="flex items-center gap-0.5 overflow-x-auto scrollbar-none">
                            <button
                                onClick={handlePDF}
                                className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium text-slate-400 hover:text-white hover:bg-white/8 transition shrink-0"
                                title="Download PDF"
                            >
                                <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3M3 17V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                                </svg>
                                PDF
                            </button>

                            <button
                                onClick={handlePrint}
                                className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium text-slate-400 hover:text-white hover:bg-white/8 transition shrink-0"
                                title="Print"
                            >
                                <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                                </svg>
                                Print
                            </button>

                            <button
                                onClick={handleEmail}
                                className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium text-slate-400 hover:text-white hover:bg-white/8 transition shrink-0"
                                title="Send via email"
                            >
                                <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                </svg>
                                Email
                            </button>

                            <button
                                onClick={handleCopy}
                                className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium transition shrink-0 ${copyDone ? 'text-emerald-400' : 'text-slate-400 hover:text-white hover:bg-white/8'}`}
                                title="Copy to clipboard"
                            >
                                {copyDone ? (
                                    <>
                                        <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                                        </svg>
                                        Copied!
                                    </>
                                ) : (
                                    <>
                                        <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                        </svg>
                                        Copy
                                    </>
                                )}
                            </button>
                        </div>

                        {/* Right side: word count + done */}
                        <div className="flex items-center gap-3 shrink-0">
                            <span className="text-[11px] text-slate-600 hidden sm:block">{wordCount} words</span>
                            <button
                                onClick={mode === 'edit' && isDirty ? () => { doSave(); onClose(); } : onClose}
                                className="inline-flex items-center gap-1.5 rounded-xl px-4 py-2 text-sm font-semibold text-white bg-teal-600 hover:bg-teal-500 transition shadow-lg shadow-teal-900/30"
                            >
                                {mode === 'edit' && isDirty ? 'Save & Close' : 'Done'}
                            </button>
                        </div>
                    </div>
                </div>

                <div style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }} />
            </div>
        </div>
    );
};

export default FlashNotesViewer;
